<template>
  <div :class="classes()">
    <Row>
      <Col class="u-margin__bottom--3@l-down" :xs="12" :l="6">
        <div :class="`${el}__wrapper`">
          <Copy
            :staticClass="`${el}__headline`"
            element="div"
            size="l"
            color="primaryBlue"
            bold
            centered
          >
            {{ $t('careLevelCalculator.analysis.statistic.overview.headline') }}
          </Copy>

          <div :class="`${el}__row ${el}__rowBars u-padding__top--3`">
            <div
              v-for="(item, idx) in pointsFromModules.filter((p) => !p.ignore)"
              :key="`pointsFromModules-${idx}`"
              :class="`${el}__col`"
            >
              <Bar :el="el" :height="item.max * 6" :percent="item.percent" />
              <Icon
                :class="`${el}__moduleIcon`"
                :name="`module-${map[idx].replace('/', '-')}`"
                color="primaryBlue"
              />
              <Copy element="div" color="primaryBlue" centered :staticClass="`${el}__module`">
                M{{ map[idx] }}
              </Copy>
            </div>
          </div>

          <div :class="`${el}__labels`">
            <Copy element="div" size="s" :staticClass="`${el}__label ${el}__label--primaryBlue`">
              {{ $t('careLevelCalculator.analysis.statistic.overview.label1') }}
            </Copy>
            <Copy
              element="div"
              size="s"
              :staticClass="`${el}__label ${el}__label--secondaryMintLight`"
            >
              {{ $t('careLevelCalculator.analysis.statistic.overview.label2') }}
            </Copy>
          </div>
          <div>
            <Copy
              element="div"
              centered
              staticClass="u-margin__top--1"
              size="s"
              color="primaryBlue"
            >
              {{ $t('careLevelCalculator.analysis.statistic.overview.description') }}
            </Copy>
          </div>
        </div>
      </Col>

      <Col :xs="12" :l="6">
        <div :class="`${el}__wrapper`">
          <Copy
            :staticClass="`${el}__headline`"
            element="div"
            size="l"
            color="primaryBlue"
            bold
            centered
          >
            {{ $t('careLevelCalculator.analysis.statistic.carousel.headline') }}
          </Copy>

          <div :class="`${el}__swiper`">
            <Button
              iconName="arrow-left-s-line"
              type="secondary"
              @click="slidePrev"
              :disabled="activeIndex === 0"
              :class="`${el}__button ${el}__button--left`"
            />
            <Swiper
              ref="swiper"
              @swiper="onSwiper"
              @slideChange="onSlideChange"
              :allow-touch-move="!$matchMedia.s"
            >
              <SwiperSlide
                v-for="(item, idx) in pointsFromModules"
                :key="`pointsFromModulesSwiper-${idx}`"
                :class="`${el}__swiperSlide`"
              >
                <div :class="`${el}__swiperSlideContent`">
                  <Bar
                    v-if="$matchMedia.s"
                    :el="el"
                    :class="`${el}__swiperSlideContentBar`"
                    :height="item.max * 6"
                    :percent="item.percent"
                  />

                  <div :class="`${el}__swiperSlideContentText`">
                    <div :class="`${el}__swiperSlideContentTextTop`">
                      <Copy
                        :staticClass="`${el}__swiperHeadline`"
                        element="div"
                        color="primaryBlue"
                        bold
                      >
                        {{ $t(`careLevelCalculator.analysis.statistic.modul${idx + 1}.headline`) }}
                      </Copy>
                      <Icon
                        :class="`${el}__swiperModuleIcon`"
                        :name="`module-${idx + 1}`"
                        color="primaryBlue"
                      />
                    </div>

                    <div :class="`${el}__swiperSlideContentTextBottom`">
                      <Copy v-if="item.ignore" element="div">
                        <span
                          v-html="
                            $t('careLevelCalculator.analysis.statistic.notice', {
                              value1: idx === 1 ? '3' : '2',
                              value2: idx === 1 ? '2' : '3',
                            })
                          "
                        />
                      </Copy>
                      <Copy
                        :staticClass="`${el}__swiperSlideContentTextBottomPoints`"
                        element="span"
                      >
                        <strong>
                          {{ formatNumber(item.userPoints) }}
                        </strong>
                        {{
                          $t('careLevelCalculator.analysis.statistic.points', { value: item.max })
                        }}
                      </Copy>
                    </div>
                  </div>
                </div>
                <Divider class="u-margin__vertical--3" dotted />
                <Copy element="div" centered>
                  <span v-html="$t(`careLevelCalculator.analysis.statistic.result${item.level}`)" />
                </Copy>
              </SwiperSlide>
            </Swiper>
            <Button
              iconName="arrow-right-s-line"
              type="secondary"
              @click="slideNext"
              :disabled="pointsFromModules.length - 1 === activeIndex"
              :class="`${el}__button ${el}__button--right`"
            />
          </div>
          <div :class="`${el}__swiperIndicator`">
            <span
              v-for="(item, idx) in pointsFromModules"
              :key="`pointsFromModulesSwiperIndicator-${idx}`"
              :class="{
                [`${el}__swiperIndicatorItem`]: true,
                [`${el}__swiperIndicatorItem--current`]: activeIndex === idx,
              }"
              @click="slideTo(idx)"
            />
          </div>
        </div>
      </Col>
    </Row>
  </div>
</template>

<script>
import { Swiper, SwiperSlide } from 'swiper/vue';
import Copy from '@/objects/Copy';
import Button from '@/objects/Button';
import Divider from '@/objects/Divider';
import Icon from '@/objects/Icon';
import Row from '@/components/Grid/Row';
import Col from '@/components/Grid/Col';

import Bar from './subcomponents/Bar';

import { formatNumber } from '@/helpers/formatter';

import { ratingPoints } from '@/content/services/CareLevelCalculator/points';
import careLevelMixin from '@/mixins/careLevel';

export default {
  name: 'CareLevelModuleStatistic',
  mixins: [careLevelMixin],
  props: {},
  components: {
    Copy,
    Button,
    Divider,
    Icon,
    Bar,
    Row,
    Col,
    Swiper,
    SwiperSlide,
  },
  computed: {
    pointsFromModules() {
      if (!this.modulePoints || !this.modulePoints[1]) {
        return [];
      }

      let ignore2Or3 = 1;
      if (this.modulePoints[1].gewichtet > this.modulePoints[2].gewichtet) {
        ignore2Or3 = 2;
      }

      const pointsFromModules = [];
      ratingPoints.map((rp, i) => {
        const max = rp[rp.length - 1].gewichtet;
        const userPoints = this.modulePoints[i].gewichtet;

        pointsFromModules.push({
          userPoints,
          max,
          percent: (100 / max) * userPoints,
          ignore: i === ignore2Or3,
          level: rp.findIndex((r) => r.gewichtet === userPoints),
        });
      });

      return pointsFromModules;
    },
  },
  data() {
    return {
      formatNumber,
      activeIndex: 0,
      swiperInstance: null,
      el: 'c-careLevelModuleStatistic',
      classes: () => {
        const el = this.el;
        return {
          [el]: true,
        };
      },
      map: ['1', '2/3', '4', '5', '6'],
    };
  },
  methods: {
    // Capture swiper instance when it's initialized
    // See docs: https://swiperjs.com/vue
    onSwiper(swiper) {
      this.swiperInstance = swiper;
    },

    slideNext() {
      if (this.swiperInstance) {
        this.swiperInstance.slideNext();
        this.$emit('onTrackingClick', 'SliderClick-rechts', 'element');
      }
    },

    slidePrev() {
      if (this.swiperInstance) {
        this.swiperInstance.slidePrev();
        this.$emit('onTrackingClick', 'SliderClick-links', 'element');
      }
    },

    slideTo(idx) {
      if (this.swiperInstance) {
        this.swiperInstance.slideTo(idx);
      }
    },

    onSlideChange(swiper) {
      // Use the swiper parameter or fallback to stored instance
      const swiperRef = swiper || this.swiperInstance;
      if (swiperRef) {
        this.activeIndex = swiperRef.activeIndex;
      }
    },
  },
};
</script>
